import { Menu as MenuIcon, Person as PersonIcon, Visibility, VisibilityOff } from "@mui/icons-material";
import {
  AppBar,
  Box,
  Button,
  IconButton,
  ListItemIcon,
  MenuItem,
  Toolbar,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { FunctionComponent } from "react";
import { Permissions } from "../api/Permissions";
import { AbsolutePages } from "../constants/Sitemap";
import { useAuth, WithForwardedAuth } from "../hooks/Auth";
import { useBreakpoints } from "../hooks/Breakpoints";
import { useCommissionsVisibility } from "../hooks/specialized/CommissionVisibility";
import { getFullName } from "../utils/Specialized";
import AnchoredMenu from "./generic/AnchoredMenu";
import LinkButton from "./generic/LinkButton";
import LinkMenuItem from "./generic/LinkMenuItem";
import Spacer from "./generic/Spacer";

const CurrentUser: FunctionComponent<WithForwardedAuth> = ({ auth }) => {
  const { mdOrUp } = useBreakpoints();
  const comVisibility = useCommissionsVisibility();

  if (!auth.user)
    return (
      <LinkButton to={AbsolutePages.login} color="inherit">
        Connexion
      </LinkButton>
    );

  const logout = () => auth.signOut();

  return (
    <AnchoredMenu
      anchor={
        mdOrUp ? (
          <Button color="inherit" startIcon={<PersonIcon />}>
            {getFullName(auth.user)}
          </Button>
        ) : (
          <Button color="inherit">
            <PersonIcon />
          </Button>
        )
      }
    >
      {mdOrUp ? null : <MenuItem disabled>{getFullName(auth.user)}</MenuItem>}
      <LinkMenuItem to={AbsolutePages.currentUserAccount}>Mon compte</LinkMenuItem>
      {auth.isSeller && (
        <MenuItem
          onClick={(e) => {
            e.preventDefault();
            comVisibility.toggle();
          }}
        >
          <ListItemIcon>{comVisibility.showCommissions ? <Visibility /> : <VisibilityOff />}</ListItemIcon>
        </MenuItem>
      )}
      <MenuItem onClick={logout}>Déconnexion</MenuItem>
    </AnchoredMenu>
  );
};

////////////////

const CurrentUserLinks: FunctionComponent<WithForwardedAuth> = ({ auth }) => {
  const theme = useTheme();
  const xs = useMediaQuery(theme.breakpoints.only("xs"));

  const links = [];

  if (auth.isSeller) {
    links.push({ link: AbsolutePages.seller.buyers, label: "Mes contacts" });
    links.push({ link: AbsolutePages.seller.options, label: "Mes options" });
    links.push({ link: AbsolutePages.seller.purchases, label: "Mes réservations" });
    links.push({ link: AbsolutePages.seller.mandates, label: "Mes mandats" });
  } else {
    if (auth.hasPermission(Permissions.canManageUsers))
      links.push({ link: AbsolutePages.admin.users, label: "Utilisateurs" });
    if (auth.hasPermission(Permissions.canManagePrograms))
      links.push({
        link: AbsolutePages.admin.sellersControl,
        label: "Mandataires",
      });
    if (auth.user) links.push({ link: AbsolutePages.admin.buyers, label: "Contacts" });
    if (auth.hasPermission(Permissions.canValidateBuyers) || auth.hasPermission(Permissions.canValidateOptions))
      links.push({
        link: AbsolutePages.admin.pendingValidations,
        label: "Validations",
      });
    if (auth.hasPermission(Permissions.canEditFiscalDevices))
      links.push({ link: AbsolutePages.admin.fiscalDevices, label: "Dispositifs" });
    if (auth.hasPermission(Permissions.canSendNotifications))
      links.push({ link: AbsolutePages.admin.pendingNotifications, label: "Notifications" });
    if (auth.hasPermission(Permissions.canEnableDocusign) || auth.hasPermission(Permissions.canEnableAr24))
      links.push({ link: AbsolutePages.admin.thirdPartyApis, label: "APIs externes" });
  }

  if (links.length === 0) return null;

  return xs ? (
    <AnchoredMenu
      anchor={
        <IconButton color="inherit">
          <MenuIcon />
        </IconButton>
      }
    >
      {links.map((l, idx) => (
        <LinkMenuItem key={idx} to={l.link}>
          {l.label}
        </LinkMenuItem>
      ))}
    </AnchoredMenu>
  ) : (
    <Box>
      {links.map((l, idx) => (
        <LinkButton key={idx} to={l.link} color="inherit" sx={{ ml: 2 }}>
          {l.label}
        </LinkButton>
      ))}
    </Box>
  );
};

////////////////

const Header: FunctionComponent = () => {
  const auth = useAuth();

  return (
    <AppBar position="static" color="appBar">
      <Toolbar>
        <LinkButton to="/">
          <img alt="logo" src={`${process.env.PUBLIC_URL}/img/logo.svg`} height="52px" />
        </LinkButton>
        {!auth.loaded ? null : (
          <>
            <CurrentUserLinks auth={auth} />
            <Spacer />
            <CurrentUser auth={auth} />
          </>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Header;
